// pick.ts — screen-tap → gaussian picking for the Viewer's measure tool. A tap
// unprojects to a world ray; the picked gaussian is the nearest one along the
// ray (smallest ray depth) whose centre lies within max(2σ of its largest
// axis, a few screen pixels) of the ray. The server then snaps the picked
// point properly via /measure.

import type { SplatCloud } from './format';
import { mat4Invert, mat4Multiply, transformPoint, vec3Normalize, vec3Sub } from './math';
import type { Mat4, Vec3 } from './math';

export interface Ray {
  origin: Vec3;
  dir: Vec3; // unit
}

export interface PickHit {
  index: number;
  id: number;
  point: Vec3; // gaussian centre, world metres
  t: number; // distance along the ray
  missM: number; // perpendicular distance from the ray
}

export interface PickOptions {
  labels?: readonly number[]; // empty/undefined = all labels
  minOpacity?: number; // default 0.2
  tolerancePx?: number; // default 8
  fovy: number; // rad, for the pixel tolerance
  viewportHeightPx: number;
}

// rayFromScreen unprojects CSS pixel (x,y) (origin top-left) through view/proj.
export function rayFromScreen(view: Mat4, proj: Mat4, x: number, y: number, width: number, height: number): Ray | null {
  const inv = mat4Invert(mat4Multiply(proj, view));
  if (!inv) return null;
  const nx = (x / width) * 2 - 1;
  const ny = 1 - (y / height) * 2;
  const near = transformPoint(inv, [nx, ny, -1]);
  const far = transformPoint(inv, [nx, ny, 1]);
  return { origin: near, dir: vec3Normalize(vec3Sub(far, near)) };
}

// pickGaussian returns the first gaussian the ray hits, or null.
export function pickGaussian(cloud: SplatCloud, ray: Ray, opts: PickOptions): PickHit | null {
  const want = opts.labels && opts.labels.length ? new Set(opts.labels) : null;
  const minOpacity = opts.minOpacity ?? 0.2;
  const tolPerMetre = ((opts.tolerancePx ?? 8) * 2 * Math.tan(opts.fovy / 2)) / Math.max(1, opts.viewportHeightPx);
  const [ox, oy, oz] = ray.origin;
  const [dx, dy, dz] = ray.dir;
  let best: PickHit | null = null;
  for (let i = 0; i < cloud.count; i++) {
    if (want && !want.has(cloud.label[i])) continue;
    if (cloud.opacity[i] < minOpacity) continue;
    const px = cloud.pos[i * 3] - ox, py = cloud.pos[i * 3 + 1] - oy, pz = cloud.pos[i * 3 + 2] - oz;
    const t = px * dx + py * dy + pz * dz;
    if (t <= 0) continue;
    if (best && t >= best.t) continue;
    const qx = px - t * dx, qy = py - t * dy, qz = pz - t * dz;
    const miss = Math.hypot(qx, qy, qz);
    const sigma = Math.exp(Math.max(cloud.logScale[i * 3], cloud.logScale[i * 3 + 1], cloud.logScale[i * 3 + 2]));
    const tol = Math.max(2 * sigma, tolPerMetre * t);
    if (miss > tol) continue;
    best = {
      index: i,
      id: cloud.ids[i],
      point: [cloud.pos[i * 3], cloud.pos[i * 3 + 1], cloud.pos[i * 3 + 2]],
      t,
      missM: miss,
    };
  }
  return best;
}

// nearestScreenPoint returns the index of the projected point nearest (x, y)
// within maxPx, or -1. Null entries (behind the camera) never match. The
// Viewer uses it to pick a keyframe off the trajectory line.
export function nearestScreenPoint(screen: readonly ([number, number] | null)[], x: number, y: number, maxPx: number): number {
  let best = -1, bestD = maxPx;
  screen.forEach((p, i) => {
    if (!p) return;
    const d = Math.hypot(p[0] - x, p[1] - y);
    if (d <= bestD) {
      best = i;
      bestD = d;
    }
  });
  return best;
}
